<template>
  <div class="produto-detalhes-page">
    <Header />
    
    <div v-if="carregando" class="loading">
      <div class="spinner"></div>
      <p>Carregando produto...</p>
    </div>

    <div v-else-if="erro" class="error-message">
      <p>{{ erro }}</p>
      <button @click="carregarProduto" class="btn-retry">Tentar novamente</button>
    </div>

    <div v-else-if="produto" class="container">
      <button type="button" class="back-link" @click="voltar">
        <svg class="back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"/>
          <polyline points="12 19 5 12 12 5"/>
        </svg>
        Voltar
      </button>
      <div class="breadcrumb">
        <router-link to="/floricultura" class="breadcrumb-link">Floricultura</router-link>
        <span class="breadcrumb-separator">/</span>
        <span class="breadcrumb-current">{{ produto.nome }}</span>
      </div>

      <div class="produto-content">
        <div class="produto-image-section">
          <div class="produto-image">
            <img 
              v-if="imagemProduto && !imageError" 
              :src="imagemProduto" 
              :alt="produto.nome"
              @error="onImageError"
            />
            <img 
              v-else-if="imagemPadrao" 
              :src="imagemPadrao" 
              :alt="produto.nome"
            />
            <div v-else class="image-placeholder">
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 16L8.586 11.414C9.367 10.633 10.633 10.633 11.414 11.414L16 16M14 14L15.586 12.414C16.367 11.633 17.633 11.633 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="produto-info-section">
          <h1 class="produto-title">{{ produto.nome }}</h1>
          <p class="produto-desc">{{ produto.descricao }}</p>
          <p class="produto-type">{{ produto.tipo }}</p>
          
          <div class="produto-price-section">
            <span class="produto-price">R$ {{ formatPrice(produto.valor) }}</span>
          </div>

          <div class="produto-details">
            <div class="detail-item">
              <span class="detail-label">Fornecedor:</span>
              <span class="detail-value">{{ produto.fornecedor }}</span>
            </div>
            <div class="detail-item" v-if="categoriaNome">
              <span class="detail-label">Categoria:</span>
              <span class="detail-value">{{ categoriaNome }}</span>
            </div>
          </div>

          <div class="produto-actions">
            <button 
              class="btn-add-cart" 
              @click="adicionarAoCarrinho"
              :disabled="produto.qtdEstoque === 0"
            >
              Adicionar ao Carrinho
            </button>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import Footer from '../components/Footer.vue'
import produtoService from '../services/produtoService'
import categoriaService from '../services/categoriaService'
import carrinhoStore from '../store/carrinho'

export default {
  name: 'ProdutoDetalhes',
  components: {
    Header,
    Footer
  },
  data() {
    return {
      produto: null,
      categorias: [],
      carregando: false,
      erro: null,
      imageError: false
    }
  },
  computed: {
    imagemProduto() {
      if (this.imageError || !this.produto?.imagem) {
        return null
      }
      
      // Se a imagem já é uma URL completa (http/https), retornar como está
      if (this.produto.imagem.startsWith('http://') || this.produto.imagem.startsWith('https://')) {
        return this.produto.imagem
      }
      
      // Se a imagem é base64, adicionar o prefixo data URI se necessário
      if (this.produto.imagem.startsWith('data:')) {
        return this.produto.imagem
      }
      
      // Se é base64 sem prefixo, adicionar o prefixo
      return `data:image/jpeg;base64,${this.produto.imagem}`
    },
    imagemPadrao() {
      if (!this.produto) return null
      
      const categoria = this.categorias.find(c => c.id === this.produto.categoriaId)
      const nomeCategoria = categoria ? categoria.descricao.toLowerCase() : ''
      const nomeProduto = this.produto.descricao ? this.produto.descricao.toLowerCase() : ''
      
      const mapeamentoImagens = {
        'coroa': 'coroa.jpg',
        'coroas': 'coroa.jpg',
        'urna': 'urna.jpg',
        'urnas': 'urna.jpg',
        'cremação': 'cremacao.jpg',
        'cremacao': 'cremacao.jpg',
        'cremações': 'cremacao.jpg',
        'jazigo': 'jazigo.jpg',
        'jazigos': 'jazigo.jpg',
        'plano funerário': 'plano.jpg',
        'planos funerários': 'plano.jpg',
        'planos funerarios': 'plano.jpg',
        'buque': 'buque.jpg',
        'buquê': 'buque.jpg',
        'buques': 'buque.jpg',
        'buquês': 'buque.jpg',
        'vaso': 'vaso.jpg',
        'vasos': 'vaso.jpg',
        'arranjo': 'arranjo.jpg',
        'arranjos': 'arranjo.jpg',
        'arranjo floral': 'arranjo.jpg',
        'arranjos florais': 'arranjo.jpg'
      }
      
      for (const [key, imagem] of Object.entries(mapeamentoImagens)) {
        if (nomeCategoria.includes(key)) {
          return `/img/${imagem}`
        }
      }
      
      for (const [key, imagem] of Object.entries(mapeamentoImagens)) {
        if (nomeProduto.includes(key)) {
          return `/img/${imagem}`
        }
      }
      
      return null
    },
    categoriaNome() {
      if (!this.produto) return null
      const categoria = this.categorias.find(c => c.id === this.produto.categoriaId)
      return categoria ? categoria.descricao : null
    }
  },
  mounted() {
    this.carregarProduto()
  },
  watch: {
    '$route'(to) {
      if (to.name === 'ProdutoDetalhes') {
        this.carregarProduto()
      }
    }
  },
  methods: {
    async carregarProduto() {
      this.carregando = true
      this.erro = null
      this.imageError = false

      try {
        const produtoId = this.$route.params.id
        
        // Carregar categorias
        this.categorias = await categoriaService.listarCategorias()
        
        // Carregar produto
        this.produto = await produtoService.buscarProduto(produtoId)
      } catch (error) {
        console.error('Erro ao carregar produto:', error)
        this.erro = 'Produto não encontrado ou erro ao carregar.'
      } finally {
        this.carregando = false
      }
    },
    formatPrice(value) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    onImageError() {
      this.imageError = true
    },
    voltar() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/floricultura')
      }
    },
    adicionarAoCarrinho() {
      if (this.produto.qtdEstoque === 0) {
        alert('Produto sem estoque disponível')
        return
      }
      
      // Garantir que a imagem seja passada corretamente
      let imagemParaSalvar = null
      
      // Primeiro tentar imagem do produto (pode ser base64 ou URL)
      if (this.imagemProduto) {
        // Se é base64 com prefixo data:, remover o prefixo para salvar apenas o base64
        if (this.imagemProduto.startsWith('data:image')) {
          imagemParaSalvar = this.imagemProduto.split(',')[1] || this.imagemProduto
        } else {
          imagemParaSalvar = this.imagemProduto
        }
      } 
      // Se não tem imagem do produto, usar imagem padrão (URL)
      else if (this.imagemPadrao) {
        imagemParaSalvar = this.imagemPadrao
      }
      // Se não tem nenhuma, tentar usar a imagem do produto original (pode estar em base64)
      else if (this.produto.imagem) {
        // Se já é base64 sem prefixo, usar direto
        if (!this.produto.imagem.startsWith('data:') && !this.produto.imagem.startsWith('http')) {
          imagemParaSalvar = this.produto.imagem
        } else if (this.produto.imagem.startsWith('data:image')) {
          imagemParaSalvar = this.produto.imagem.split(',')[1] || this.produto.imagem
        } else {
          imagemParaSalvar = this.produto.imagem
        }
      }
      
      const produtoComImagem = {
        ...this.produto,
        imagem: imagemParaSalvar
      }
      
      carrinhoStore.adicionarItem(produtoComImagem, 1)
      this.$router.push('/carrinho')
    }
  }
}
</script>

<style scoped>
.produto-detalhes-page {
  min-height: 100vh;
  background: #ffffff;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 0;
  padding-bottom: 60px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 0;
  border: none;
  background: none;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.2s, gap 0.2s;
}

.back-link:hover {
  color: #34894d;
}

.back-link:hover .back-icon {
  transform: translateX(-2px);
}

.back-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  transition: transform 0.2s;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #6b7280;
}

.breadcrumb-link {
  color: #34894d;
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover {
  color: #2d7a42;
  text-decoration: underline;
}

.breadcrumb-separator {
  color: #9ca3af;
}

.breadcrumb-current {
  color: #111827;
  font-weight: 500;
}

.produto-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;
}

.produto-image-section {
  position: sticky;
  top: 100px;
}

.produto-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.produto-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  color: #9ca3af;
  opacity: 0.5;
}

.produto-info-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.produto-title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.2;
}

.produto-desc {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.produto-type {
  font-size: 18px;
  color: #6b7280;
  margin: 0;
}

.produto-price-section {
  padding: 20px 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.produto-price {
  font-size: 36px;
  font-weight: 700;
  color: #34894d;
}

.produto-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
}

.detail-item {
  display: flex;
  gap: 12px;
}

.detail-label {
  font-weight: 600;
  color: #4b5563;
  min-width: 120px;
}

.detail-value {
  color: #111827;
}

.detail-value.low-stock {
  color: #f56565;
  font-weight: 600;
}

.produto-actions {
  padding-top: 24px;
}

.btn-add-cart {
  width: 100%;
  background: #34894d;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-cart:hover:not(:disabled) {
  background: #2d7a42;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(52, 137, 77, 0.3);
}

.btn-add-cart:active:not(:disabled) {
  transform: translateY(0);
}

.btn-add-cart:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 20px;
  color: #718096;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top-color: #34894d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  padding: 120px 20px;
  color: #f56565;
}

.btn-retry {
  margin-top: 20px;
  padding: 12px 24px;
  background: #34894d;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-retry:hover {
  background: #2d7a42;
}

@media (max-width: 968px) {
  .produto-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .produto-image-section {
    position: static;
  }

  .produto-title {
    font-size: 28px;
  }

  .produto-price {
    font-size: 32px;
  }
}
</style>

